import { Lock } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthLayout from '../components/AuthLayout'
import { AuthService } from '../utils/authService'

export default function ResetPassword() {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    otp: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    // Get email from localStorage (stored during forgot password flow)
    const storedEmail = localStorage.getItem('resetPasswordEmail')
    if (storedEmail) {
      setEmail(storedEmail)
    } else {
      // If no email found, redirect to forgot password
      navigate('/forget-password')
    }
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validation
    if (!formData.otp.trim()) {
      setError('OTP is required')
      return
    }

    if (!formData.password.trim()) {
      setError('Password is required')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    setIsLoading(true)

    try {
      await AuthService.resetPassword({
        email,
        otp: formData.otp,
        newPassword: formData.password,
      })

      // Clear stored email
      localStorage.removeItem('resetPasswordEmail')

      // Navigate to login with success message
      navigate('/login', {
        state: {
          message:
            'Password reset successfully! Please login with your new password.',
        },
      })
    } catch (err: any) {
      // Extract the exact error message from API response
      let errorMessage = 'Failed to reset password. Please try again.'

      if (err.response?.message) {
        errorMessage = err.response.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <div className='w-full max-w-[400px] mx-auto'>
        <div className='mb-8'>
          <h1 className='text-[36px] font-[700] text-[#181D27] mb-3 leading-[38px] text-start'>
            Set New Password
          </h1>
          <p className='text-[16px] text-[#667085] text-start font-[400] leading-[24px]'>
            Enter the OTP sent to your email address:{' '}
            <b className='font-[700] text-[#181D27]'>{email || 'your email'}</b>{' '}
            and set your new password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          {error && (
            <div className='p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md'>
              {error}
            </div>
          )}

          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='otp'
              className='block text-[14px] font-medium text-[#344054] mb-[6px]'
            >
              OTP Code
            </label>
            <div className='relative w-full'>
              <input
                id='otp'
                type='text'
                placeholder='Enter 6-digit OTP'
                value={formData.otp}
                onChange={e =>
                  setFormData({ ...formData, otp: e.target.value })
                }
                className='w-full h-[44px] px-[14px] border border-[#D0D5DD] rounded-[8px] text-[16px] text-black placeholder:text-[#667085] outline-none bg-white'
                maxLength={6}
                required
                disabled={isLoading}
              />
            </div>
          </div>
          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='password'
              className='block text-[14px] font-medium text-[#344054] mb-[6px]'
            >
              New Password
            </label>
            <div className='relative w-full'>
              <Lock className='absolute left-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]' />
              <input
                id='password'
                type='password'
                placeholder='••••••••'
                value={formData.password}
                onChange={e =>
                  setFormData({ ...formData, password: e.target.value })
                }
                className='w-full h-[44px] pl-[44px] pr-[14px] border border-[#D0D5DD] rounded-[8px] text-[16px] text-black placeholder:text-[#667085] outline-none bg-white'
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='confirmPassword'
              className='block text-[14px] font-medium text-[#344054] mb-[6px]'
            >
              Confirm Password
            </label>
            <div className='relative w-full'>
              <Lock className='absolute left-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]' />
              <input
                id='confirmPassword'
                type='password'
                placeholder='••••••••'
                value={formData.confirmPassword}
                onChange={e =>
                  setFormData({ ...formData, confirmPassword: e.target.value })
                }
                className='w-full h-[44px] pl-[44px] pr-[14px] border border-[#D0D5DD] rounded-[8px] text-[16px] text-black placeholder:text-[#667085] outline-none bg-white'
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <button
            type='submit'
            disabled={isLoading}
            className='w-full h-[44px] bg-[#001B38] cursor-pointer text-white text-[16px] font-semibold rounded-[8px] transition-colors duration-200 mt-6 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </AuthLayout>
  )
}
